import { create } from 'zustand';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { logInfo, logWarn } from '../lib/logger';
import { errMessage, isTransientNetworkError } from '../lib/net/transientError';
import { classifyCallError, isTransientDeviceError } from '../lib/telephony/twilioErrors';
import type { Call, CallPhase, SmsMessage } from '../types/telephony';

/**
 * Minimal slice of @twilio/voice-sdk we rely on. The SDK is an optional runtime
 * dependency loaded dynamically in initDevice() (see the indirect import), so
 * the build doesn't hard-require it and these interfaces type our usage.
 */
interface TwilioCall {
  disconnect(): void;
  mute(shouldMute: boolean): void;
  sendDigits(digits: string): void;
  accept(): void;
  reject(): void;
  parameters?: Record<string, string>;
  on(event: string, handler: (...args: unknown[]) => void): void;
}
interface TwilioDevice {
  register(): Promise<void>;
  connect(options: { params: Record<string, string> }): Promise<TwilioCall>;
  on(event: string, handler: (...args: unknown[]) => void): void;
  updateToken(token: string): void;
  destroy(): void;
}
interface TwilioDeviceCtor {
  new (token: string, options?: Record<string, unknown>): TwilioDevice;
}

// Non-serializable handles live outside the store.
let device: TwilioDevice | null = null;
let activeCall: TwilioCall | null = null;
// The seat read in flight: the ~11 components that mount useCanUseTelephony share one request.
let seatRequest: Promise<void> | null = null;

// crm_calls / crm_sms_messages aren't in the generated Database types yet
// (migration not applied at build time). Read them through an untyped client
// until `supabase gen types` is re-run post-migration.
const db = supabase as unknown as SupabaseClient;

interface CommsOpts {
  contactId?: string | null;
  accountId?: string | null;
  /** The Otto mission the call is placed for (its call request card): stored on the call. */
  missionThreadId?: string | null;
}

/**
 * The custom parameters the softphone sends with a call. Twilio forwards them to the TwiML
 * webhook, which links the call to its contact and mission by key; only strings, and only
 * those that are set.
 */
function softphoneParams(to: string, opts?: CommsOpts): Record<string, string> {
  const params: Record<string, string> = { To: to, mode: 'outbound' };
  if (opts?.contactId) params.contactId = opts.contactId;
  if (opts?.missionThreadId) params.missionThreadId = opts.missionThreadId;
  return params;
}

interface TelephonyState {
  phase: CallPhase;
  deviceReady: boolean;
  activeNumber: string | null;
  activeContactId: string | null;
  muted: boolean;
  error: string | null;
  /** Why the last call ended when the other side ended it (refused, busy, no answer): not an error. */
  endNotice: string | null;
  callDirection: 'inbound' | 'outbound' | null;
  canUse: boolean;
  seatLoaded: boolean;

  initDevice: () => Promise<void>;
  startCall: (to: string, opts?: CommsOpts) => Promise<void>;
  bridgeCall: (to: string, opts?: CommsOpts) => Promise<boolean>;
  hangup: () => void;
  acceptCall: () => void;
  rejectCall: () => void;
  toggleMute: () => void;
  /** DTMF during an active call (a switchboard's "tapez 1"). */
  sendDigits: (digits: string) => void;
  sendSms: (to: string, body: string, opts?: CommsOpts) => Promise<boolean>;
  fetchContactComms: (contactId: string) => Promise<{ calls: Call[]; sms: SmsMessage[] }>;
  fetchAccountComms: (accountId: string) => Promise<{ calls: Call[]; sms: SmsMessage[] }>;
  getRecordingUrl: (callId: string) => Promise<string | null>;
  loadSeat: () => Promise<void>;
  clearError: () => void;
}

const getAuthHeaders = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');
  return {
    Authorization: `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
    apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
  };
};

const getBaseUrl = () => `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;

const requestTwilioToken = async (): Promise<Response> =>
  fetch(`${getBaseUrl()}/telephony-token`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({}),
  });

/** Mint a fresh Twilio Voice access token (1 h TTL) for the softphone. */
export const fetchTwilioToken = async (): Promise<string> => {
  let res = await requestTwilioToken();
  // telephony-token answers 401 when getUser refuses the Supabase JWT: a session gone stale
  // while the tab slept. Refresh it once and retry; a second 401 is a real sign-out.
  if (res.status === 401) {
    const { error } = await supabase.auth.refreshSession();
    if (!error) res = await requestTwilioToken();
  }
  const data = (await res.json().catch(() => null)) as { data?: { token?: string }; error?: string } | null;
  const token = data?.data?.token;
  if (!token) throw new Error(data?.error || 'Token Twilio indisponible');
  return token;
};

/** Twilio error codes meaning the access token is invalid/expired → refreshable. */
const TOKEN_ERROR_CODES = new Set([20101, 20104, 20151, 31204, 31205]);

/**
 * The softphone is a desktop-cockpit feature. A phone can't sustain the Twilio
 * WebSocket — a backgrounded mobile tab gets its transport killed, surfacing as
 * error 31009 ("no transport available") — and standing the Device up there just
 * burns a token fetch + ringtone downloads for a surface nobody dials from. So
 * we don't initialise it on touch phones at all. Coarse pointer + narrow
 * viewport keeps desktops (and mouse-driven tablets) on the real thing.
 */
export function isMobileDevice(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return (
    window.matchMedia('(pointer: coarse)').matches &&
    window.matchMedia('(max-width: 768px)').matches
  );
}

export const useTelephonyStore = create<TelephonyState>((set, get) => ({
  phase: 'idle',
  deviceReady: false,
  activeNumber: null,
  activeContactId: null,
  muted: false,
  error: null,
  endNotice: null,
  callDirection: null,
  canUse: false,
  seatLoaded: false,

  initDevice: async () => {
    if (device) {
      set({ deviceReady: true });
      return;
    }
    // Phones can't hold the Twilio transport — don't even try (see isMobileDevice).
    if (isMobileDevice()) {
      set({ deviceReady: false, error: null });
      return;
    }
    try {
      const token = await fetchTwilioToken();

      // Literal dynamic import so Vite code-splits & bundles the SDK (a variable
      // specifier is left unbundled → fails at runtime in the browser).
      const sdk = await import('@twilio/voice-sdk');
      const DeviceCtor = sdk.Device as unknown as TwilioDeviceCtor;
      device = new DeviceCtor(token, { codecPreferences: ['opus', 'pcmu'] });

      // The SDK's EventEmitter THROWS on an unhandled 'error' emission, and the
      // 1 h token dies silently in long-lived tabs — both must be handled or the
      // softphone ends up half-dead (device set, gateway rejecting: error 31005).
      device.on('error', (err: unknown) => {
        const e = err as { code?: number; message?: string };

        // Transient signaling/transport drops self-heal via the SDK's reconnect (a
        // registered softphone holds a long-lived websocket, so blips are expected)
        // — breadcrumb, don't page, and don't surface a UI error for a blip.
        if (isTransientDeviceError(err)) {
          logInfo('media', `telephony: transient connection error (${e.code})`, { message: e.message });
          return;
        }

        // Expired/invalid token: the reactive refresh below IS the recovery, so
        // the error itself is a breadcrumb, not a page. We only warn if the
        // refresh actually fails for a non-network reason (see below).
        if (e.code !== undefined && TOKEN_ERROR_CODES.has(e.code)) {
          logInfo('media', `telephony: token error (${e.code}), refreshing`, { message: e.message });
          void (async () => {
            try {
              device?.updateToken(await fetchTwilioToken());
              set({ error: null });
            } catch (refreshErr) {
              // A refresh that fails because the network is down (Failed to
              // fetch, offline) is the user's connectivity, not our bug —
              // breadcrumb. A real failure (token endpoint 5xx/401) keeps its
              // warning. Either way the softphone is down until reload.
              if (isTransientNetworkError(refreshErr)) {
                logInfo('media', 'telephony: token refresh reporté (réseau indisponible)', { message: errMessage(refreshErr) });
              } else {
                logWarn('media', 'telephony: token refresh after device error failed', { err: refreshErr });
              }
              device?.destroy();
              device = null;
              set({ deviceReady: false, error: 'Softphone déconnecté — rechargez la page' });
            }
          })();
          return;
        }

        // Anything else is a genuine, non-self-healing device error → page it.
        logWarn('media', 'telephony: device error', { err });
        set({ error: e.message ?? 'Erreur softphone' });
      });

      // Proactive refresh: fires shortly before the 1 h token expires, so a tab
      // left open keeps receiving inbound calls and can still dial out.
      device.on('tokenWillExpire', () => {
        void (async () => {
          try {
            device?.updateToken(await fetchTwilioToken());
          } catch (e) {
            // Same rule as the reactive path: a network blip is a breadcrumb,
            // a real endpoint failure is a warning. The SDK will fire this again.
            if (isTransientNetworkError(e)) {
              logInfo('media', 'telephony: token refresh proactif reporté (réseau indisponible)', { message: errMessage(e) });
            } else {
              logWarn('media', 'telephony: token refresh failed', { err: e });
            }
          }
        })();
      });

      device.on('registered', () => set({ deviceReady: true }));
      device.on('unregistered', () => set({ deviceReady: false }));

      device.on('incoming', (call: unknown) => {
        const c = call as TwilioCall;
        activeCall = c;
        set({ phase: 'ringing', callDirection: 'inbound', activeNumber: c.parameters?.From ?? null, endNotice: null });
        const close = () => {
          activeCall = null;
          set({ phase: 'idle', callDirection: null, activeNumber: null });
        };
        c.on('accept', () => set({ phase: 'active' }));
        c.on('cancel', close); // answered on the mobile, or the caller hung up
        c.on('reject', close);
        c.on('disconnect', () => {
          activeCall = null;
          set({ phase: 'ended', muted: false });
          setTimeout(() => {
            if (get().phase === 'ended') set({ phase: 'idle', callDirection: null, activeNumber: null });
          }, 3000);
        });
        c.on('error', (e: unknown) => {
          logWarn('media', 'telephony: incoming error', { err: e });
          close();
        });
      });

      await device.register();
      set({ deviceReady: true, error: null });
    } catch (e) {
      logWarn('media', 'telephony: initDevice failed', { err: e });
      // Never keep a half-initialized device around: startCall treats a non-null
      // device as usable, and a broken one gets every call rejected by the gateway.
      device?.destroy();
      device = null;
      set({ deviceReady: false, error: errMessage(e) || 'Softphone indisponible' });
    }
  },

  startCall: async (to, opts) => {
    await get().initDevice();
    if (!device) {
      set({ error: 'Softphone indisponible' });
      return;
    }
    try {
      set({ phase: 'connecting', callDirection: 'outbound', activeNumber: to, activeContactId: opts?.contactId ?? null, error: null, endNotice: null });
      const call = await device.connect({ params: softphoneParams(to, opts) });
      activeCall = call;
      call.on('accept', () => set({ phase: 'active' }));
      call.on('disconnect', () => {
        activeCall = null;
        set({ phase: 'ended', muted: false });
        // Auto-close the panel shortly after the call ends (either side hangs up).
        setTimeout(() => {
          if (get().phase === 'ended') set({ phase: 'idle', callDirection: null, activeNumber: null, activeContactId: null, endNotice: null });
        }, 3000);
      });
      call.on('error', (err: unknown) => {
        activeCall = null;
        const outcome = classifyCallError(err);
        if (outcome.kind === 'ended') {
          // The other side refused, was busy or didn't answer: a normal end, not a bug.
          logInfo('media', `telephony: call ended by the callee (${outcome.label})`);
          set({ phase: 'ended', endNotice: outcome.label, muted: false });
        } else {
          logWarn('media', 'telephony: call error', { err });
          set({ phase: 'ended', error: 'Erreur pendant l\'appel' });
        }
        // Same auto-close as 'disconnect' — an errored call must not leave the
        // panel stranded on "Terminé" with a stale message.
        setTimeout(() => {
          if (get().phase === 'ended') {
            set({ phase: 'idle', callDirection: null, activeNumber: null, activeContactId: null, error: null, endNotice: null });
          }
        }, 3000);
      });
    } catch (e) {
      // device.connect may reject with `undefined`: never dereference the reason.
      logWarn('media', 'telephony: startCall failed', { err: e });
      set({ phase: 'idle', error: errMessage(e) || 'Appel impossible' });
    }
  },

  hangup: () => {
    activeCall?.disconnect();
    activeCall = null;
    // Also clears `error`: the panel stays mounted while an error is displayed,
    // so a hangup that leaves it set makes the close button look dead.
    set({ phase: 'idle', activeNumber: null, activeContactId: null, muted: false, callDirection: null, error: null, endNotice: null });
  },

  acceptCall: () => {
    activeCall?.accept();
    set({ phase: 'active' });
  },

  rejectCall: () => {
    activeCall?.reject();
    activeCall = null;
    set({ phase: 'idle', activeNumber: null, callDirection: null, error: null, endNotice: null });
  },

  toggleMute: () => {
    const next = !get().muted;
    activeCall?.mute(next);
    set({ muted: next });
  },

  sendDigits: (digits) => {
    if (get().phase !== 'active') return;
    activeCall?.sendDigits(digits);
  },

  bridgeCall: async (to, opts) => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${getBaseUrl()}/telephony-bridge-call`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          to,
          contactId: opts?.contactId,
          accountId: opts?.accountId,
          missionThreadId: opts?.missionThreadId,
        }),
      });
      const data = await res.json();
      if (!res.ok || data?.success === false) throw new Error(data?.error || 'Échec de l\'appel');
      return true;
    } catch (e) {
      logWarn('media', 'telephony: bridgeCall failed', { err: e });
      set({ error: errMessage(e) || 'Échec de l\'appel' });
      return false;
    }
  },

  sendSms: async (to, body, opts) => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${getBaseUrl()}/telephony-sms-send`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ to, body, contactId: opts?.contactId, accountId: opts?.accountId }),
      });
      const data = await res.json();
      if (!res.ok || data?.success === false) throw new Error(data?.error || 'Envoi SMS échoué');
      return true;
    } catch (e) {
      logWarn('media', 'telephony: sendSms failed', { err: e });
      set({ error: errMessage(e) || 'Envoi SMS échoué' });
      return false;
    }
  },

  fetchContactComms: async (contactId) => {
    const [callsRes, smsRes] = await Promise.all([
      db.from('crm_calls').select('*').eq('contact_id', contactId).order('created_at', { ascending: false }).limit(50),
      db.from('crm_sms_messages').select('*').eq('contact_id', contactId).order('created_at', { ascending: false }).limit(100),
    ]);
    return {
      calls: (callsRes.data ?? []) as Call[],
      sms: (smsRes.data ?? []) as SmsMessage[],
    };
  },

  fetchAccountComms: async (accountId) => {
    const [callsRes, smsRes] = await Promise.all([
      // Calls of the account AND of its contacts: one SQL reader shared with Otto and the
      // account dossier (get_account_calls), never a filter re-implemented here.
      db.rpc('get_account_calls', { p_account_id: accountId, p_limit: 50 }),
      db.from('crm_sms_messages').select('*').eq('account_id', accountId).order('created_at', { ascending: false }).limit(100),
    ]);
    return {
      calls: (callsRes.data ?? []) as Call[],
      sms: (smsRes.data ?? []) as SmsMessage[],
    };
  },

  getRecordingUrl: async (callId) => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${getBaseUrl()}/telephony-recording-url`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ call_id: callId }),
      });
      const data = await res.json();
      if (!res.ok) return null;
      return (data?.data?.url as string) ?? null;
    } catch (e) {
      logWarn('media', 'telephony: getRecordingUrl failed', { err: e });
      return null;
    }
  },

  loadSeat: () => {
    // Every mounted useCanUseTelephony calls this before seatLoaded turns true: one read for all.
    seatRequest ??= (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          set({ canUse: false, seatLoaded: true });
          return;
        }
        // By its key, never by the address in ilike, where `_` is a wildcard (PLAN-DETTE 5.59 f).
        const { data } = await db
          .from('telephony_seats')
          .select('can_call, enabled')
          .eq('user_id', user.id)
          .maybeSingle();
        set({ canUse: !!(data?.can_call && data?.enabled), seatLoaded: true });
      } catch (e) {
        logWarn('media', 'telephony: loadSeat failed', { err: e });
        set({ canUse: false, seatLoaded: true });
      }
    })().finally(() => {
      seatRequest = null;
    });
    return seatRequest;
  },

  clearError: () => set({ error: null }),
}));
